import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

interface QuickCheckResult {
  url: string;
  reachable: boolean;
  httpsAvailable: boolean;
  pageLoadMs: number;
  title: string;
  findings: Array<{
    checkId: string;
    description: string;
    severity: string;
    wcagCriteria: string | null;
  }>;
  htmlIssues: Array<{
    id: string;
    description: string;
    severity: string;
    wcag: string | null;
  }>;
  score: number;
  summary: string;
}

type CheckStatus = "idle" | "checking" | "done" | "error";

export function UrlChecker() {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<CheckStatus>("idle");
  const [result, setResult] = useState<QuickCheckResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    let trimmed = url.trim();
    if (!trimmed) return;

    // Auto-prepend https:// if no protocol specified
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }

    setStatus("checking");
    setResult(null);
    setErrorMsg("");

    try {
      const res = await fetch("/api/public/quick-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed }),
      });

      if (res.status === 429) {
        setStatus("error");
        setErrorMsg("Too many checks. Please try again later.");
        return;
      }

      if (!res.ok) {
        const data = (await res.json()) as { message?: string };
        throw new Error(data.message ?? "Check failed");
      }

      const data = (await res.json()) as QuickCheckResult;
      setResult(data);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  return (
    <div className="url-checker">
      <form
        className="url-checker__form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <label htmlFor="url-check-input" className="sr-only">
          Website URL
        </label>
        <input
          id="url-check-input"
          type="text"
          className="url-checker__input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Enter your website URL (e.g. example.com)"
          disabled={status === "checking"}
        />
        <button
          type="submit"
          className="btn btn--primary url-checker__btn"
          disabled={!url.trim() || status === "checking"}
        >
          {status === "checking" ? "Checking…" : "Check accessibility"}
        </button>
      </form>

      {status === "checking" && (
        <div className="url-checker__progress" role="status" aria-live="polite">
          <div className="url-checker__spinner" />
          <p>Analysing your website — this takes a few seconds…</p>
        </div>
      )}

      {status === "error" && (
        <div className="url-checker__error" role="alert">
          {errorMsg}
        </div>
      )}

      {status === "done" && result && <UrlCheckerResults result={result} />}
    </div>
  );
}

function UrlCheckerResults({ result }: { result: QuickCheckResult }) {
  const allIssues = [
    ...result.findings.map((f) => ({
      id: f.checkId,
      description: f.description,
      severity: f.severity,
      wcag: f.wcagCriteria,
    })),
    ...result.htmlIssues,
  ];

  const scoreClass =
    result.score >= 90
      ? "url-checker__score--strong"
      : result.score >= 70
        ? "url-checker__score--ok"
        : result.score >= 50
          ? "url-checker__score--warn"
          : "url-checker__score--risk";

  return (
    <div className="url-checker__results" aria-live="polite">
      <div className="url-checker__score-row">
        <div className={`url-checker__score ${scoreClass}`}>
          <span className="url-checker__score-value">{result.score}</span>
          <span className="url-checker__score-max">/100</span>
        </div>
        <div className="url-checker__score-detail">
          {result.title && (
            <p className="url-checker__page-title">{result.title}</p>
          )}
          <p className="url-checker__summary">{result.summary}</p>
          <p className="url-checker__meta">
            Loaded in {result.pageLoadMs}ms · HTTPS{" "}
            {result.httpsAvailable ? "✓" : "✗"}
          </p>
        </div>
      </div>

      {allIssues.length > 0 && (
        <div className="url-checker__issues">
          <h3 className="url-checker__issues-title">
            Issues found ({allIssues.length})
          </h3>
          <ul className="url-checker__issue-list">
            {allIssues.slice(0, 8).map((issue) => (
              <li key={issue.id} className="url-checker__issue">
                <span
                  className={`url-checker__severity url-checker__severity--${issue.severity}`}
                >
                  {issue.severity}
                </span>
                <span className="url-checker__issue-desc">
                  {issue.description}
                </span>
                {issue.wcag && (
                  <span className="url-checker__wcag">WCAG {issue.wcag}</span>
                )}
              </li>
            ))}
            {allIssues.length > 8 && (
              <li className="url-checker__issue url-checker__issue--more">
                + {allIssues.length - 8} more issue(s)
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="url-checker__cta">
        <p>
          Want the full report with detailed guidance on how to fix each issue?
        </p>
        <Link to="/sign-in" className="btn btn--primary">
          Sign up free for the full report
        </Link>
      </div>

      <p className="url-checker__disclaimer">
        This is a quick automated check of your homepage only. A full scan
        covers more pages, runs browser-based testing, and provides detailed
        remediation guidance.
      </p>
    </div>
  );
}
